import { t } from '../../../lib/i18n';
import type { FinancePeriod } from '../types';
import { type RevenueBar, type RevenueTotals, formatChf } from '../utils';
import { FinancePeriodTabs } from './finance-tabs';
import { PanelEmpty } from './panel-state';
import { RevenueBarChart } from './revenue-bar-chart';

interface RevenueRow {
  key: string;
  label: string;
  /** Legend swatch — the same colour as this stream's segment in the chart. */
  dot: string;
  received: number;
  pending: number;
}

interface RevenueCardProps {
  period: FinancePeriod;
  /** The instant the period strip names its months from. */
  periodNow?: Date;
  onPeriodChange: (period: FinancePeriod) => void;
  /**
   * The card's OWN caption — the true span of the months folded into
   * `totals`/`bars`, from `revenueWindowLabel`. Deliberately NOT the summary
   * card's `periodLabel`: see that function's docblock for why the two can
   * legitimately name different spans.
   */
  windowLabel: string;
  totals: RevenueTotals;
  bars: RevenueBar[];
  /** False when the endpoint answered with no months at all. */
  hasData: boolean;
}

/**
 * «Дохід CarsFinders» — what the business earned, as distinct from what moved
 * through it.
 *
 * The distinction is the card's whole job. Everything else on this screen is
 * client money passing through us (a car, duty, VAT, delivery); this card holds
 * only the streams that are ours — the retail service fee, the dealer cut,
 * dealer subscriptions and, when a period holds one, the deposit withheld on a
 * refused car (owner decision (a); the summary's `revenueChf` counts it too).
 *
 * ── Received and pending are kept APART ─────────────────────────────────────
 * `RevenueMonth` is not a flat figure per stream. Revenue is recognized when
 * the invoice is RAISED, so each month carries a settlement split: `received`
 * is what has landed, `pending` is what was invoiced in that month and is still
 * outstanding. The headline is `received` — the money we actually have — and
 * the pending figure sits beside it rather than inside it. A single total would
 * tell an operator that money has arrived when it has only been billed.
 *
 * ── This total is NOT the summary card's «Дохід» ────────────────────────────
 * It is labelled «Виставлено за період» for exactly that reason. Two honest
 * differences: the summary's rollup keeps an undated invoiced deal (dating it
 * now) where the chart has no bucket to put it in, and `dealer_subscription` is
 * a stream here with no counterpart in the summary's `revenueChf`. Giving the
 * two figures one label is how that gap gets reported as a bug.
 */
export function RevenueCard({
  period,
  periodNow,
  onPeriodChange,
  windowLabel,
  totals,
  bars,
  hasData,
}: RevenueCardProps) {
  const rows: RevenueRow[] = [
    {
      key: 'retail',
      label: t('adminFinance.revenue.retail'),
      dot: 'bg-cf-brand',
      received: totals.received[0],
      pending: totals.pending[0],
    },
    {
      key: 'dealerCut',
      label: t('adminFinance.revenue.dealerCut'),
      dot: 'bg-cf-accent',
      received: totals.received[1],
      pending: totals.pending[1],
    },
    {
      key: 'subscription',
      label: t('adminFinance.revenue.subscription'),
      dot: 'bg-cf-ink-faint',
      received: totals.received[2],
      pending: totals.pending[2],
    },
    // Owner decision (a): the deposit withheld on a refused car. Not on the
    // artboard, so the row appears only when the period holds one.
    ...(totals.received[3] > 0 || totals.pending[3] > 0
      ? [
          {
            key: 'depositForfeit',
            label: t('adminFinance.revenue.depositForfeit'),
            dot: 'bg-cf-info',
            received: totals.received[3],
            pending: totals.pending[3],
          },
        ]
      : []),
  ];

  return (
    <section className="flex flex-col gap-3 rounded-cf-lg border border-cf-line bg-cf-surface p-4 lg:gap-3.5 lg:px-[22px] lg:py-5">
      <div className="flex items-center justify-between gap-2 lg:items-baseline">
        <h2 className="text-[16px] font-semibold lg:text-[18px] lg:tracking-[-0.02em]">
          {t('adminFinance.revenue.title')}
        </h2>
        <span
          data-testid="revenue-window-label"
          className="hidden font-mono text-[10px] tracking-[0.08em] text-cf-ink-subtle uppercase lg:inline"
        >
          {windowLabel}
        </span>
        <div className="lg:hidden">
          <FinancePeriodTabs active={period} now={periodNow} onChange={onPeriodChange} />
        </div>
      </div>

      {!hasData ? (
        <PanelEmpty messageKey="adminFinance.revenue.empty" />
      ) : (
        <>
          <div>
            <div className="font-mono text-[28px] leading-none font-medium text-cf-ink lg:text-[32px]">
              {formatChf(totals.receivedTotal)}
            </div>
            <div className="mt-1 text-[12px] text-cf-ink-subtle lg:mt-1.5">
              {t('adminFinance.revenue.subLead')}{' '}
              <span className="text-cf-warn">{formatChf(totals.pendingTotal)}</span>{' '}
              {t('adminFinance.revenue.subTail')}
            </div>
            <div className="mt-0.5 font-mono text-[11px] text-cf-ink-subtle">
              {t('adminFinance.revenue.chartedTotal', {
                amount: formatChf(totals.chartedTotal),
              })}
            </div>
          </div>

          <RevenueBarChart bars={bars} />

          <div className="flex flex-col">
            {rows.map((row) => (
              <div
                key={row.key}
                className="flex justify-between gap-2 border-b border-cf-line-soft py-2 text-[13px] lg:py-[9px]"
              >
                <span className="flex items-center gap-2 text-cf-ink-muted">
                  <span className={`h-2 w-2 rounded-cf-xs ${row.dot}`} aria-hidden />
                  {row.label}
                </span>
                <span className="text-right">
                  <span className="font-mono text-cf-ink">{formatChf(row.received)}</span>
                  {row.pending > 0 && (
                    <span
                      className="ml-1.5 font-mono text-[11px] text-cf-warn"
                      title={t('adminFinance.revenue.pendingHint')}
                    >
                      +{formatChf(row.pending)}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <p className="hidden text-[11px] leading-[1.5] text-cf-ink-subtle lg:block">
        {t('adminFinance.revenue.footnote')}
      </p>
    </section>
  );
}
